import { SUCCESS_ALERT_TYPES, type ActivityAlertType, type AlertSeverity, type IActivityAlertItem } from '@apis/activityAlerts';

/**
 * A row the card renders: either one alert on its own, or several of the same
 * type collapsed behind a count. Grouping is a presentation concern only —
 * alerts are stored and served one per item.
 */
export type IAlertRow =
  | { kind: 'single'; alert: IActivityAlertItem }
  | { kind: 'group'; type: ActivityAlertType; severity: AlertSeverity; alerts: IActivityAlertItem[] };

const SEVERITY_RANK: Record<AlertSeverity, number> = { info: 0, warning: 1, critical: 2 };

/** Display order within a colour band: red (critical) → yellow (warning) →
 *  grey (info) → green (success types). Lower sorts first. */
function displayRank(severity: AlertSeverity, type: ActivityAlertType): number {
  if (SUCCESS_ALERT_TYPES.has(type)) return 3;
  return 2 - SEVERITY_RANK[severity]; // critical 0, warning 1, info 2
}

/**
 * Collapse same-type alerts within one day, then order the rows for scanning:
 * colour bands lead — red, then yellow, then grey, then green — and within
 * each band the group rows (aggregates) come before the singles. The sort is
 * stable, so within one band+kind the API's newest-first order is preserved.
 */
export function groupAlertsByType(alerts: IActivityAlertItem[]): IAlertRow[] {
  const byType = new Map<ActivityAlertType, IActivityAlertItem[]>();
  const order: ActivityAlertType[] = [];
  for (const alert of alerts) {
    const bucket = byType.get(alert.type);
    if (bucket) {
      bucket.push(alert);
    } else {
      byType.set(alert.type, [alert]);
      order.push(alert.type);
    }
  }

  const rows: IAlertRow[] = order.map((type) => {
    const members = byType.get(type)!;
    if (members.length === 1) return { kind: 'single', alert: members[0]! };
    const severity = members.reduce<AlertSeverity>(
      (worst, alert) => (SEVERITY_RANK[alert.severity] > SEVERITY_RANK[worst] ? alert.severity : worst),
      'info',
    );
    return { kind: 'group', type, severity, alerts: members };
  });

  const rowRank = (row: IAlertRow) =>
    row.kind === 'group'
      ? displayRank(row.severity, row.type)
      : displayRank(row.alert.severity, row.alert.type);
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      // Colour band first: red → yellow → grey → green…
      const rankDelta = rowRank(a.row) - rowRank(b.row);
      if (rankDelta !== 0) return rankDelta;
      // …groups before singles within a band…
      const kindDelta = Number(a.row.kind === 'single') - Number(b.row.kind === 'single');
      if (kindDelta !== 0) return kindDelta;
      // …stable within a band+kind.
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}
